import React from 'react';
import { StandardTableActions, ColoredBadge } from '../../components/ui';

import { Column } from '../../components/DataTable/DataTable';
import { TruncatedText } from '../../components/DataTable/TruncatedText';
import { InvoicePayment } from './useData';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';
import { ThemeColors } from '../../contexts/ThemeContext';

const PAYMENT_STATUS_COLOR_FULL = '#22c55e';
const PAYMENT_STATUS_COLOR_PARTIAL = '#eab308';

const renderPaymentStatusBadge = (label: string, isFull: boolean, className?: string) => (
  <ColoredBadge
    label={label}
    color={isFull ? PAYMENT_STATUS_COLOR_FULL : PAYMENT_STATUS_COLOR_PARTIAL}
    className={className}
  />
);

const formatInvoiceMoney = (amount?: number | null) => {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  return new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

interface ClientInvoiceListRow {
  invoiceId: number;
  InvoiceNo?: string | null;
  InvoiceDate?: string | null;
  invoiceAmount?: number | null;
  paymentTotal?: number | null;
}

const filterInvoicesForPaymentSelect = (item: ClientInvoiceListRow, formData: Record<string, unknown>): boolean => {
  const pinned = formData.invoiceId;
  if (pinned !== undefined && pinned !== null && String(pinned) !== '' && String(item.invoiceId) === String(pinned)) {
    return true;
  }
  const pt = item.paymentTotal;
  const amt = item.invoiceAmount;
  if (amt == null) return true;
  if (pt == null || pt === undefined) return true;
  return pt < amt;
};

const sortInvoicesByDateDesc = (items: ClientInvoiceListRow[]): ClientInvoiceListRow[] =>
  [...items].sort((a, b) => {
    const ta = a.InvoiceDate ? new Date(a.InvoiceDate).getTime() : 0;
    const tb = b.InvoiceDate ? new Date(b.InvoiceDate).getTime() : 0;
    return tb - ta;
  });

const formatEurInline = (amount: number) =>
  new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

const mapInvoiceRowToSelectOption = (inv: ClientInvoiceListRow) => {
  const no = inv.InvoiceNo || `Invoice #${inv.invoiceId}`;
  const dateStr = inv.InvoiceDate ? formatDateGlobal(inv.InvoiceDate) : '';
  const amt = inv.invoiceAmount;
  const pt = inv.paymentTotal;
  const parts: string[] = [];
  if (dateStr) parts.push(dateStr);
  parts.push(`Total ${formatInvoiceMoney(amt)}`);
  if (pt != null && amt != null) {
    const open = Math.max(0, amt - pt);
    parts.push(`Paid ${formatInvoiceMoney(pt)} · Open ${formatInvoiceMoney(open)}`);
  } else {
    parts.push('Unpaid');
  }
  return {
    value: inv.invoiceId,
    label: no,
    description: parts.join(' · '),
  };
};

const invoiceOptionsEndpointFromClient = (formData: Record<string, unknown>): string | null => {
  const raw = formData.clientId;
  if (raw === undefined || raw === null || String(raw) === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return `/crm-clients-invoices/all_of/${n}`;
};

/** Detail route for client invoice records (matches client-invoices module). */
const clientInvoiceDetailPath = (invoiceId: number) => `/client-invoices/${invoiceId}`;

// Module Metadata
export const moduleConfig = {
  // Module Information
  module: {
    title: 'Invoice Payments',
    description: 'Manage invoice payments database',
    permissionModuleKey: 'invoice_payments',
    route: {
      list: '/invoice-payments',
      new: '/invoice-payments/new',
      detail: (id: number) => `/invoice-payments/${id}`,
      edit: (id: number) => `/invoice-payments/${id}/edit`,
    },
    // API Endpoints
    api: {
      list: '/crm-clients-invoice-payments',
      detail: (id: number) => `/crm-clients-invoice-payments/${id}`,
    },
  },

  // Pagination Settings
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  // Search Settings
  search: {
    placeholder: 'Search invoice payments...',
  },

  // Filter Groups Configuration
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'invoiceNumber', 'clientName', 'paymentDate', 'paymentAmount', 'paymentStatus'
      ],
    },
    {
      name: 'advanced',
      label: 'Advanced',
      icon: '⚙️',
      fields: [
        'invoiceDate', 'paymentNotes'
      ],
    },
  ],

  // Filter Configuration
  filters: {
    fields: [
      {
        key: 'invoiceNumber',
        label: 'Invoice Number',
        placeholder: 'All Invoice Numbers',
        getValue: (item: InvoicePayment) => item.invoice?.InvoiceNo,
        options: [],
      },
      {
        key: 'clientName',
        label: 'Client',
        placeholder: 'All Clients',
        type: 'select' as const,
        getValue: (item: InvoicePayment) => item.invoice?.client?.clientName || '',
        options: [],
        apiEndpoint: '/crm-clients',
        labelField: 'clientName',
      },
      {
        key: 'paymentDate',
        label: 'Payment Date',
        placeholder: 'All Payment Dates',
        type: 'date' as const,
        getValue: (item: InvoicePayment) =>
          item.paymentDate
            ? formatDateGlobal(item.paymentDate)
            : undefined,
        options: [],
      },
      {
        key: 'paymentAmount',
        label: 'Payment Amount',
        placeholder: 'All Amounts',
        type: 'number' as const,
        getValue: (item: InvoicePayment) => item.paymentAmount?.toString(),
        options: [],
      },
      {
        key: 'paymentStatus',
        label: 'Payment Status',
        placeholder: 'All Status',
        type: 'text' as const,
        getValue: (item: InvoicePayment) => {
          const invoiceAmount = item.invoice?.invoiceAmount || 0;
          const paymentAmount = parseFloat(String(item.paymentAmount)) || 0;
          return paymentAmount >= invoiceAmount ? 'Full' : 'Partial';
        },
        options: ['Full', 'Partial'],
      },
      {
        key: 'invoiceDate',
        label: 'Invoice Date',
        placeholder: 'All Invoice Dates',
        type: 'date' as const,
        getValue: (item: InvoicePayment) =>
          item.invoice?.InvoiceDate
            ? formatDateGlobal(item.invoice.InvoiceDate)
            : undefined,
        options: [],
      },
      {
        key: 'paymentNotes',
        label: 'Notes',
        placeholder: 'All Notes',
        getValue: (item: InvoicePayment) => item.paymentNotes,
        options: [],
      },
    ],
    // Filter logic functions
    applyFilter: (
      items: InvoicePayment[],
      filters: {
        invoiceNumber: string[] | string;
        clientName: string[] | string;
        paymentDate: string[];
        paymentAmount: string[];
        paymentStatus: string[];
        invoiceDate: string[];
        paymentNotes: string[] | string;
      },
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      const ranges = dateRanges || {};
      return items.filter((item) => {
        // Invoice Number filter
        const invoiceNumberFilter = filters.invoiceNumber;
        if (typeof invoiceNumberFilter === 'string' && invoiceNumberFilter.trim() !== '') {
          const value = (item.invoice?.InvoiceNo || '').toLowerCase();
          if (!value.includes(invoiceNumberFilter.toLowerCase())) return false;
        } else if (Array.isArray(invoiceNumberFilter) && invoiceNumberFilter.length > 0) {
          const value = (item.invoice?.InvoiceNo || '').toLowerCase();
          const matches = invoiceNumberFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Client Name filter
        const clientNameFilter = filters.clientName;
        if (typeof clientNameFilter === 'string' && clientNameFilter.trim() !== '') {
          const value = (item.invoice?.client?.clientName || '').toLowerCase();
          if (!value.includes(clientNameFilter.toLowerCase())) return false;
        } else if (Array.isArray(clientNameFilter) && clientNameFilter.length > 0) {
          const value = (item.invoice?.client?.clientName || '').toLowerCase();
          const matches = clientNameFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Payment Date filter
        if (filters.paymentDate.length > 0) {
          if (ranges.paymentDate) {
            const range = ranges.paymentDate;
            if (range.start && range.end && item.paymentDate) {
              const itemDate = new Date(item.paymentDate);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.paymentDate
              ? formatDateGlobal(item.paymentDate)
              : '';
            const matches = filters.paymentDate.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // Payment Amount filter
        if (filters.paymentAmount.length > 0) {
          const value = item.paymentAmount?.toString() || '';
          const matches = filters.paymentAmount.some((filter) => {
            return value === filter || filter === value;
          });
          if (!matches) return false;
        }

        // Payment Status filter
        if (filters.paymentStatus.length > 0) {
          const invoiceAmount = item.invoice?.invoiceAmount || 0;
          const paymentAmount = parseFloat(String(item.paymentAmount)) || 0;
          const status = paymentAmount >= invoiceAmount ? 'Full' : 'Partial';
          if (!filters.paymentStatus.includes(status)) return false;
        }

        // Invoice Date filter
        if (filters.invoiceDate.length > 0) {
          if (ranges.invoiceDate) {
            const range = ranges.invoiceDate;
            if (range.start && range.end && item.invoice?.InvoiceDate) {
              const itemDate = new Date(item.invoice.InvoiceDate);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.invoice?.InvoiceDate
              ? formatDateGlobal(item.invoice.InvoiceDate)
              : '';
            const matches = filters.invoiceDate.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // Payment Notes filter
        const notesFilter = filters.paymentNotes;
        if (typeof notesFilter === 'string' && notesFilter.trim() !== '') {
          const value = (item.paymentNotes || '').toLowerCase();
          if (!value.includes(notesFilter.toLowerCase())) return false;
        } else if (Array.isArray(notesFilter) && notesFilter.length > 0) {
          const value = (item.paymentNotes || '').toLowerCase();
          const matches = notesFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        return true;
      });
    },
  },

  // Toolbar Actions Configuration
  toolbar: {
    actions: [
      {
        key: 'download',
        icon: 'download',
        title: 'Download',
        showTitle: false,
        variant: 'default' as const,
        dropdown: [
          {
            key: 'export-excel',
            label: 'Export to Excel',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }

              try {
                const { exportToExcel } = await import('../../components/exports');
                await exportToExcel(exportData);
              } catch (error: any) {
                console.error('Failed to export to Excel:', error);
                throw new Error(error?.message || 'Failed to export to Excel. Please check the console for details.');
              }
            },
          },
        ],
      },
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Payment',
        showTitle: false,
        variant: 'primary' as const,
        href: '/invoice-payments/new',
      },
    ],
  },

  // Table Configuration
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: InvoicePayment) => {
      window.location.href = `/invoice-payments/${item.paymentId}`;
    },
    keyExtractor: (item: InvoicePayment) => item.paymentId,
  },

  // Empty States
  emptyStates: {
    noData: 'Get started by creating a new invoice payment.',
    noSearchResults: 'No invoice payments found matching your search criteria.',
    noFilterResults: 'No invoice payments match the selected filters.',
  },

  // Delete Confirmation
  delete: {
    title: 'Delete Payment',
    message: 'Are you sure you want to delete this payment? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete payment. Please try again.',
    handler: async (id: number) => {
      const { deleteInvoicePayment } = await import('../../services/mutations');
      await deleteInvoicePayment(id);
    },
  },

  // Utility Functions
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
    formatCurrency: (amount?: number) => {
      if (amount == null) return '-';
      return new Intl.NumberFormat('en-IE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    },
  },

  // Detail View Configuration
  detail: {
    subtitle: 'Payment Details',
    notFoundTitle: 'Payment not found',
    notFoundMessage: 'The payment you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Payments',
    defaultTitle: 'Unnamed Payment',
    getTitle: (item: InvoicePayment) => {
      return item.invoice?.InvoiceNo 
        ? `Payment for Invoice ${item.invoice.InvoiceNo}`
        : `Payment #${item.paymentId}`;
    },
  },

  // Form Configuration
  form: {
    new: {
      title: 'New Payment',
      subtitle: 'Add a new invoice payment',
      submitText: 'Create Payment',
    },
    edit: {
      title: 'Edit Payment',
      submitText: 'Update Payment',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading payment...',
    errorMessage: 'Failed to save payment. Please try again.',
    create: async (data: Partial<InvoicePayment>) => {
      const { createInvoicePayment } = await import('../../services/mutations');
      return await createInvoicePayment(data);
    },
    update: async (id: number, data: Partial<InvoicePayment>) => {
      const { updateInvoicePayment } = await import('../../services/mutations');
      return await updateInvoicePayment(id, data);
    },
  },

  // Unified Field Configuration
  fields: [
    {
      name: 'clientId',
      label: 'Client',
      type: 'select',
      placeholder: 'Select a client',
      required: true,
      options: [],
      apiEndpoint: '/crm-clients',
      valueField: 'clientId',
      labelField: 'clientName',
      validation: {
        required: true,
      },
    },
    {
      name: 'invoiceId',
      label: 'Invoice',
      type: 'select',
      placeholder: 'Select an invoice',
      required: true,
      options: [],
      optionsEndpointFromFormData: invoiceOptionsEndpointFromClient,
      optionsDependOnFields: ['clientId'],
      valueField: 'invoiceId',
      filterDynamicOptionItem: (item, formData) =>
        filterInvoicesForPaymentSelect(item as ClientInvoiceListRow, formData as Record<string, unknown>),
      sortDynamicList: (items) => sortInvoicesByDateDesc(items as ClientInvoiceListRow[]),
      mapApiItemToSelectOption: (item) => mapInvoiceRowToSelectOption(item as ClientInvoiceListRow),
      validation: {
        required: true,
      },
    },
    {
      name: 'paymentDate',
      label: 'Payment Date',
      type: 'date',
      placeholder: 'Select payment date',
      validation: {
        required: false,
      },
    },
    {
      name: 'paymentAmount',
      label: 'Payment Amount',
      type: 'number',
      required: true,
      placeholder: 'Enter payment amount',
      labelAfter: ({ getFormData, onChange }) => {
        if (!getFormData) return null;
        const fd = getFormData() as Record<string, unknown>;
        const inv = fd.invoice as { invoiceAmount?: unknown } | undefined;
        const rawInvoice =
          inv?.invoiceAmount !== undefined && inv?.invoiceAmount !== null
            ? inv.invoiceAmount
            : fd.invoiceAmount;
        const invoiceAmount = typeof rawInvoice === 'number' ? rawInvoice : Number(rawInvoice);
        if (!Number.isFinite(invoiceAmount) || invoiceAmount <= 0) {
          return null;
        }
        const rawPaid = fd.invoicePaymentsTotal;
        const totalPaidNum =
          rawPaid !== undefined && rawPaid !== null
            ? typeof rawPaid === 'number'
              ? rawPaid
              : Number(rawPaid)
            : 0;
        const totalPaid = Number.isFinite(totalPaidNum) ? totalPaidNum : 0;
        const remaining = Math.max(0, Math.round((invoiceAmount - totalPaid) * 100) / 100);
        if (remaining <= 0) {
          return null;
        }
        return (
          <button
            type="button"
            className="text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline decoration-blue-600/60 hover:decoration-blue-800"
            onClick={() => onChange(remaining)}
          >
            Full payment ({formatEurInline(remaining)})
          </button>
        );
      },
      validation: {
        required: true,
        min: 0,
      },
    },
    {
      name: 'paymentNotes',
      label: 'Notes',
      type: 'textarea',
      placeholder: 'Enter payment notes (optional)',
      rows: 4,
      validation: {
        maxLength: 1000,
      },
    },
  ] as FieldConfig[],

  // Form Layout Configuration (crm-tasks style: gridCols + fieldSpans)
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'payment',
        title: 'Payment Information',
        icon: '💳',
        gridCols: 'md:grid-cols-12',
        fields: ['clientId', 'invoiceId', 'paymentDate', 'paymentAmount', 'paymentNotes'],
        fieldSpans: {
          clientId: 'md:col-span-6',
          invoiceId: 'md:col-span-6',
          paymentDate: 'md:col-span-6',
          paymentAmount: 'md:col-span-6',
          paymentNotes: 'md:col-span-12',
        },
        collapsible: false,
      },
    ],
  } as FormLayout,
};

// Helper function to get columns
export const getColumns = (
  formatDate: (dateString?: string) => string, 
  formatCurrency: (amount?: number) => string,
  colors?: ThemeColors
): Column<InvoicePayment>[] => {
  const textColor = colors?.text || 'text-gray-900';
  const textSecondary = colors?.textSecondary || 'text-gray-700';

  return [
    {
      key: 'invoiceNumber',
      header: 'Invoice No.',
      sortable: true,
      render: (value, row) => (
        <span className={`text-sm ${textColor}`}>{row.invoice?.InvoiceNo || '-'}</span>
      ),
    },
    {
      key: 'invoiceDate',
      header: 'Invoice Date',
      sortable: true,
      render: (value, row) => (
        <span className={`text-sm ${textColor}`}>
          {row.invoice?.InvoiceDate ? formatDate(row.invoice.InvoiceDate) : '-'}
        </span>
      ),
    },
    {
      key: 'invoiceAmount',
      header: 'Invoice Amount',
      sortable: true,
      render: (value, row) => (
        <span className={`text-sm ${textColor} text-right`}>
          {formatCurrency(row.invoice?.invoiceAmount)}
        </span>
      ),
    },
    {
      key: 'clientName',
      header: 'Client Name',
      sortable: true,
      render: (value, row) => (
        <TruncatedText
          text={row.invoice?.client?.clientName || '-'}
          maxLength={50}
          className={`text-sm ${textSecondary}`}
          showTooltip={true}
        />
      ),
    },
    {
      key: 'paymentDate',
      header: 'Payment Date',
      sortable: true,
      render: (value) => (
        <span className={`text-sm ${textColor}`}>{value ? formatDate(value) : '-'}</span>
      ),
    },
    {
      key: 'paymentAmount',
      header: 'Payment',
      sortable: true,
      render: (value, row) => {
        const invoiceAmount = row.invoice?.invoiceAmount || 0;
        const paymentAmount = parseFloat(String(value)) || 0;
        const isFullPayment = paymentAmount >= invoiceAmount;
        const statusText = isFullPayment ? 'Full' : 'Partial';

        return (
          <div className="flex items-center gap-2">
            {renderPaymentStatusBadge(statusText, isFullPayment)}
            {!isFullPayment && (
              <span className={`text-sm font-semibold ${textColor}`}>
                {formatCurrency(paymentAmount)}
              </span>
            )}
          </div>
        );
      },
    },
  {
    key: 'paymentNotes',
    header: 'Notes',
    sortable: true,
    render: (value) => (
      <TruncatedText
        text={value || ''}
        maxLength={50}
        className={`text-sm ${textSecondary}`}
        showTooltip={true}
      />
    ),
  },
  ];
};

// Helper function to get detail sections
export const getDetailSections = (
  item: InvoicePayment,
  formatDate: (dateString?: string) => string,
  formatCurrency: (amount?: number) => string
) => {
  const invoiceAmount = item.invoice?.invoiceAmount || 0;
  const paymentAmount = parseFloat(String(item.paymentAmount)) || 0;
  const isFullPayment = paymentAmount >= invoiceAmount;

  return [
    {
      title: 'Payment Information',
      icon: '💳',
      fields: [
        {
          label: 'Payment ID',
          value: item.paymentId.toString(),
        },
        {
          label: 'Invoice Number',
          value: item.invoice?.InvoiceNo || '-',
        },
        {
          label: 'Invoice Date',
          value: item.invoice?.InvoiceDate ? formatDate(item.invoice.InvoiceDate) : '-',
        },
        {
          label: 'Invoice Amount',
          value: formatCurrency(item.invoice?.invoiceAmount),
        },
        {
          label: 'Client Name',
          value: item.invoice?.client?.clientName || '-',
        },
        {
          label: 'Payment Date',
          value: item.paymentDate ? formatDate(item.paymentDate) : '-',
        },
        {
          label: 'Payment Amount',
          value: formatCurrency(item.paymentAmount),
        },
        {
          label: 'Payment Status',
          value: renderPaymentStatusBadge(isFullPayment ? 'Full Payment' : 'Partial Payment', isFullPayment),
        },
        {
          label: 'Notes',
          value: item.paymentNotes || '-',
        },
      ],
    },
  ];
};

// Helper function to get status badges
export const getStatusBadges = (item: InvoicePayment, _colors?: ThemeColors) => {
  const invoiceAmount = item.invoice?.invoiceAmount || 0;
  const paymentAmount = parseFloat(String(item.paymentAmount)) || 0;
  const isFullPayment = paymentAmount >= invoiceAmount;
  return renderPaymentStatusBadge(
    isFullPayment ? 'Full Payment' : 'Partial Payment',
    isFullPayment,
    'px-3 py-1 text-sm font-semibold'
  );
};

// Helper function to render toolbar icon
export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'download':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      );
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

// Helper function to render table action buttons
export const getTableActions = (
  item: InvoicePayment,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.paymentId)}
      editTo={moduleConfig.module.route.edit(item.paymentId)}
      onDelete={() => onDelete(item.paymentId)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};


export const getInitialFormData = (): Partial<InvoicePayment> => {
  return {
    clientId: undefined,
    invoiceId: undefined,
    paymentDate: undefined,
    paymentAmount: undefined,
    paymentNotes: '',
  };
};

// Helper function to map item data to form data
export const mapItemToFormData = (item: InvoicePayment): Partial<InvoicePayment> => {
  return {
    clientId: item.invoice?.clientId != null ? item.invoice.clientId : undefined,
    invoiceId: item.invoiceId,
    paymentDate: item.paymentDate,
    paymentAmount: item.paymentAmount,
    paymentNotes: item.paymentNotes || '',
    invoice: item.invoice,
    invoicePaymentsTotal: item.invoicePaymentsTotal,
  };
};

export type ModuleConfig = typeof moduleConfig;

